/**
 * CORE — deterministic vector/quaternion math (pure JS doubles).
 *
 * Every operation here works on plain `[x, y, z]` / `[x, y, z, w]` tuples from
 * `@/types` and returns fresh tuples. There is NO dependency on Rapier, Three,
 * the DOM, or any platform global. All arithmetic is IEEE-754 double precision,
 * which is bit-identical across V8 (browser), Node, and the Cloudflare Workers
 * runtime — that is the bedrock of cross-environment determinism.
 *
 * Owned by: Core. Internal helper (not part of the public surface).
 */

import type { Vec3, Quat } from '@/types';

export const ZERO3: Vec3 = [0, 0, 0];
export const IDENTITY_QUAT: Quat = [0, 0, 0, 1];

export function v3(x: number, y: number, z: number): Vec3 {
  return [x, y, z];
}

export function add(a: Vec3, b: Vec3): Vec3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}

export function sub(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

export function scale(a: Vec3, s: number): Vec3 {
  return [a[0] * s, a[1] * s, a[2] * s];
}

export function addScaled(a: Vec3, b: Vec3, s: number): Vec3 {
  return [a[0] + b[0] * s, a[1] + b[1] * s, a[2] + b[2] * s];
}

export function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

export function cross(a: Vec3, b: Vec3): Vec3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

export function length(a: Vec3): number {
  return Math.sqrt(a[0] * a[0] + a[1] * a[1] + a[2] * a[2]);
}

export function lengthSq(a: Vec3): number {
  return a[0] * a[0] + a[1] * a[1] + a[2] * a[2];
}

/** Horizontal (XZ-plane) speed; the "forward roll" magnitude. */
export function lengthXZ(a: Vec3): number {
  return Math.sqrt(a[0] * a[0] + a[2] * a[2]);
}

export function normalize(a: Vec3): Vec3 {
  const len = length(a);
  if (len === 0) return [0, 0, 0];
  const inv = 1 / len;
  return [a[0] * inv, a[1] * inv, a[2] * inv];
}

export function distance(a: Vec3, b: Vec3): number {
  return length(sub(a, b));
}

export function distanceSq(a: Vec3, b: Vec3): number {
  return lengthSq(sub(a, b));
}

/** Clamp a scalar to [min, max]. */
export function clamp(x: number, min: number, max: number): number {
  return x < min ? min : x > max ? max : x;
}

/** Clamp the magnitude of a vector to at most `maxLen` (deterministic). */
export function clampLength(a: Vec3, maxLen: number): Vec3 {
  const lenSq = lengthSq(a);
  const maxSq = maxLen * maxLen;
  if (lenSq <= maxSq || lenSq === 0) return a;
  const s = maxLen / Math.sqrt(lenSq);
  return [a[0] * s, a[1] * s, a[2] * s];
}

/* ---- Quaternion helpers (Y-up, right-handed; matches Three.js) ----------- */

export function quatNormalize(q: Quat): Quat {
  const len = Math.sqrt(q[0] * q[0] + q[1] * q[1] + q[2] * q[2] + q[3] * q[3]);
  if (len === 0) return [0, 0, 0, 1];
  const inv = 1 / len;
  return [q[0] * inv, q[1] * inv, q[2] * inv, q[3] * inv];
}

export function quatMul(a: Quat, b: Quat): Quat {
  const [ax, ay, az, aw] = a;
  const [bx, by, bz, bw] = b;
  return [
    aw * bx + ax * bw + ay * bz - az * by,
    aw * by - ax * bz + ay * bw + az * bx,
    aw * bz + ax * by - ay * bx + az * bw,
    aw * bw - ax * bx - ay * by - az * bz,
  ];
}

/** Quaternion from an axis (need not be unit) and angle in radians. */
export function quatFromAxisAngle(axis: Vec3, angle: number): Quat {
  const n = normalize(axis);
  const half = angle * 0.5;
  const s = Math.sin(half);
  return [n[0] * s, n[1] * s, n[2] * s, Math.cos(half)];
}

/** Rotate a vector by a quaternion (v' = q * v * q⁻¹), expanded form. */
export function quatRotate(q: Quat, v: Vec3): Vec3 {
  const [x, y, z, w] = q;
  const [vx, vy, vz] = v;
  // t = 2 * cross(q.xyz, v)
  const tx = 2 * (y * vz - z * vy);
  const ty = 2 * (z * vx - x * vz);
  const tz = 2 * (x * vy - y * vx);
  // v' = v + w * t + cross(q.xyz, t)
  return [
    vx + w * tx + (y * tz - z * ty),
    vy + w * ty + (z * tx - x * tz),
    vz + w * tz + (x * ty - y * tx),
  ];
}

/** Conjugate (inverse for unit quaternions). */
export function quatConjugate(q: Quat): Quat {
  return [-q[0], -q[1], -q[2], q[3]];
}
